import React, { useState } from 'react';
import PageShell from '../../components/PageShell';
import {
  Eyebrow, SectionHeading, IconBadge, ContextStrip, InfoCard,
  GradientButton, CTABanner, VideoCard,
} from '../../components/common/Primitives';
import VideoLightbox from '../../components/common/VideoLightbox';
import LicenceFeeHighlight from '../../components/common/LicenceFeeHighlight';
import { EXPLORE_OVERVIEW as E } from '../../lib/mockExplore';
import { FEATURED_VIDEO_ID, youtubeThumb } from '../../lib/moduleVideos';
import { PlayCircle, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function ExploreOverview() {
  const [heroOpen, setHeroOpen] = useState(false);
  return (
    <PageShell>
      {/* ================ HERO ================ */}
      <section className="px-6 pt-12 pb-8" data-testid="explore-hero">
        <div className="mx-auto max-w-[1240px]">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.15fr_1fr] items-start">
            <div>
              <Eyebrow color="purple">{E.hero.eyebrow}</Eyebrow>
              <h1 className="mt-3 text-[40px] md:text-[54px] font-extrabold leading-[1.06] tracking-tight text-navy">{E.hero.title}</h1>
              <p className="mt-5 max-w-xl text-[15.5px] leading-relaxed text-ink-500">{E.hero.desc}</p>
              <div className="mt-6"><LicenceFeeHighlight /></div>
              <div className="mt-6 flex flex-wrap gap-3">
                <GradientButton to={E.hero.ctaPrimary.to}   data-testid="explore-hero-cta-primary">{E.hero.ctaPrimary.label}</GradientButton>
                <GradientButton to={E.hero.ctaSecondary.to} variant="outline" data-testid="explore-hero-cta-secondary">{E.hero.ctaSecondary.label}</GradientButton>
              </div>
            </div>
            <div className="rounded-3xl border border-canvas-200 bg-gradient-to-br from-purple-50 via-white to-mint-50 p-6 md:p-8 shadow-soft">
              <button
                type="button"
                aria-label="Play Gyan Dhara in 75 seconds"
                onClick={() => setHeroOpen(true)}
                data-testid="explore-hero-video-play"
                className="group relative block w-full aspect-video rounded-2xl overflow-hidden focus:outline-none focus:ring-4 focus:ring-purple/30"
              >
                <img src={youtubeThumb(FEATURED_VIDEO_ID, 'maxresdefault')} alt="Gyan Dhara in 75 seconds" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-black/35 group-hover:bg-black/25 transition-colors" />
                <span className="absolute inset-0 grid place-items-center">
                  <span className="grid h-16 w-16 place-items-center rounded-full bg-white/95 text-navy shadow-card play-glow group-hover:scale-105 transition-transform"><PlayCircle className="h-9 w-9" /></span>
                </span>
              </button>
              <div className="mt-4 text-[11.5px] font-bold tracking-widest text-purple">{E.hero.videoTag}</div>
              <p className="mt-1 text-[13px] text-ink-500 leading-relaxed">{E.hero.videoDesc}</p>
            </div>
          </div>
          <div className="mt-10"><ContextStrip items={E.strip} /></div>
        </div>
      </section>

      {/* ================ ONE COMPLETE LEARNING ECOSYSTEM ================ */}
      <section className="px-6 pt-4 pb-8" data-testid="explore-ecosystem-4pillars">
        <div className="mx-auto max-w-[1240px]">
          <div className="rounded-3xl border border-canvas-200 bg-white p-6 md:p-7 shadow-soft">
            <div>
              <div className="text-[11.5px] font-bold tracking-widest text-purple">ONE COMPLETE LEARNING ECOSYSTEM</div>
              <div className="mt-1 text-[19px] md:text-[22px] font-extrabold text-navy leading-snug">VR Experiences · Web Learning Hub · AI Learning · Analytics</div>
              <p className="mt-1 max-w-2xl text-[13px] text-ink-500">Four connected layers work together to help every learner understand, practise, ask and progress.</p>
            </div>
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {[
                { title: 'VR Experiences',   desc: 'Immersive Grade 8–9 modules for Science and Mathematics.',                              color: 'purple', icon: 'Headset' },
                { title: 'Web Learning Hub', desc: 'Chapter videos, resources and unlimited quiz practice.',                                color: 'royal',  icon: 'Monitor' },
                { title: 'AI Learning',      desc: 'Gyan Mitra answers questions verbally in English and Hindi.',                          color: 'cyan',   icon: 'Bot' },
                { title: 'Analytics',        desc: 'Time spent, chapter progress and conceptual, applied and theoretical scores.',          color: 'mint',   icon: 'BarChart3' },
              ].map((p, i) => (
                <div key={i} className="relative rounded-2xl border border-canvas-200 bg-canvas-100 p-4 transition-transform hover:-translate-y-0.5 hover:shadow-soft">
                  <IconBadge icon={p.icon} color={p.color} />
                  <div className="mt-3 text-[13.5px] font-bold text-navy">{p.title}</div>
                  <p className="mt-1 text-[12px] leading-snug text-ink-500">{p.desc}</p>
                  {i < 3 && (
                    <span className="hidden lg:flex absolute -right-2 top-1/2 -translate-y-1/2 h-6 w-6 items-center justify-center rounded-full bg-white ring-1 ring-canvas-200 text-purple">
                      <ArrowRight className="h-3 w-3" />
                    </span>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/explore/vr-experiences"   className="text-[13px] font-bold text-purple hover:underline" data-testid="explore-ecosystem-link-vr">Explore VR Experiences →</Link>
              <span className="text-ink-400">·</span>
              <Link to="/explore/web-learning-hub" className="text-[13px] font-bold text-purple hover:underline" data-testid="explore-ecosystem-link-hub">Explore Web Learning Hub →</Link>
            </div>
          </div>
        </div>
      </section>

      {/* ================ WHAT IT IS ================ */}
      <section className="px-6 py-16" data-testid="explore-what">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={E.what.eyebrow} title={E.what.title} desc={E.what.desc} />
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {E.what.items.map((w, i) => (
              <InfoCard key={i} letter={w.letter} color={w.color} title={w.title} desc={w.desc} />
            ))}
          </div>
        </div>
      </section>

      {/* ================ LEARNING EXPERIENCES ================ */}
      <section className="px-6 py-16 bg-white" data-testid="explore-experiences">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={E.experiences.eyebrow} title={E.experiences.title} desc={E.experiences.desc} />
          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
            {E.experiences.items.map((m, i) => (
              <VideoCard key={i} title={m.title} chapter={m.subject} status="Available" gradient={['from-purple to-royal','from-royal to-cyan','from-cyan to-mint','from-mint to-royal','from-cyan to-purple','from-royal to-purple'][i % 6]} />
            ))}
          </div>
          <div className="mt-6 rounded-2xl border border-purple-100 bg-gradient-to-br from-purple-50/60 via-white to-cyan-50/60 p-5">
            <div className="text-[13px] font-bold text-purple">{E.experiences.beyondTitle}</div>
            <p className="mt-2 text-[13px] text-ink-700">{E.experiences.beyond}</p>
            <p className="mt-2 text-[12px] text-ink-500 italic">{E.experiences.note}</p>
          </div>
          <div className="mt-6"><GradientButton to="/explore/vr-experiences" data-testid="explore-experiences-cta">View all VR experiences</GradientButton></div>
        </div>
      </section>

      {/* ================ HOW THE ECOSYSTEM CONNECTS ================ */}
      <section className="px-6 py-16" data-testid="explore-flow">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={E.flow.eyebrow} title={E.flow.title} desc={E.flow.desc} />
          <div className="mt-8 hidden lg:flex items-stretch gap-3">
            {E.flow.steps.map((s, i) => (
              <React.Fragment key={i}>
                <div className="flex-1 rounded-2xl border border-canvas-200 bg-white p-5 shadow-soft">
                  <span className={`inline-flex h-8 w-8 items-center justify-center rounded-full bg-${s.color} text-white text-[13px] font-extrabold`}>{s.n}</span>
                  <div className="mt-3 text-[14.5px] font-bold text-navy leading-snug">{s.title}</div>
                  <p className="mt-1.5 text-[12.5px] leading-relaxed text-ink-500">{s.desc}</p>
                </div>
                {i < E.flow.steps.length - 1 && <ArrowRight className="h-5 w-5 self-center shrink-0 text-ink-400" />}
              </React.Fragment>
            ))}
          </div>
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:hidden">
            {E.flow.steps.map((s, i) => (
              <div key={i} className="rounded-2xl border border-canvas-200 bg-white p-5 shadow-soft">
                <span className={`inline-flex h-8 w-8 items-center justify-center rounded-full bg-${s.color} text-white text-[13px] font-extrabold`}>{s.n}</span>
                <div className="mt-3 text-[14.5px] font-bold text-navy leading-snug">{s.title}</div>
                <p className="mt-1.5 text-[12.5px] leading-relaxed text-ink-500">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================ CHOOSE YOUR PATHWAY LATER ================ */}
      <section className="px-6 py-16 bg-white" data-testid="explore-pathways">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={E.pathways.eyebrow} title={E.pathways.title} desc={E.pathways.desc} />
          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            {E.pathways.items.map((p, i) => (
              <Link key={i} to={p.to} className="group rounded-2xl border border-canvas-200 bg-white p-5 shadow-soft transition-transform hover:-translate-y-0.5 hover:shadow-card" data-testid={`explore-pathway-${p.letter.toLowerCase()}`}>
                <span className={`inline-flex h-9 w-9 items-center justify-center rounded-full bg-${p.color} text-white text-[13px] font-extrabold`}>{p.letter}</span>
                <div className="mt-3 text-[14px] font-bold text-navy leading-snug">{p.title}</div>
                <p className="mt-1 text-[12px] leading-snug text-ink-500">{p.desc}</p>
                <div className="mt-3 text-[12px] font-bold text-purple opacity-0 group-hover:opacity-100 transition-opacity">Open pathway →</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ================ ACCESS AND COSTS ================ */}
      <section className="px-6 py-16" data-testid="explore-access-costs">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={E.access.eyebrow} title={E.access.title} desc={E.access.desc} />
          <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="rounded-2xl border border-mint/30 bg-mint-50/60 p-6 shadow-soft">
              <div className="text-[11.5px] font-bold tracking-[0.14em] text-mint">INCLUDED</div>
              <div className="mt-2 text-[16px] font-bold text-navy">{E.access.included.title}</div>
              <p className="mt-3 text-[13.5px] leading-relaxed text-ink-500">{E.access.included.items}</p>
            </div>
            <div className="rounded-2xl border border-royal/30 bg-royal-50/60 p-6 shadow-soft">
              <div className="text-[11.5px] font-bold tracking-[0.14em] text-royal">SEPARATELY FUNDED</div>
              <div className="mt-2 text-[16px] font-bold text-navy">{E.access.funded.title}</div>
              <p className="mt-3 text-[13.5px] leading-relaxed text-ink-500">{E.access.funded.items}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ================ FINAL CTA ================ */}
      <section className="px-6 pb-24" data-testid="explore-cta">
        <div className="mx-auto max-w-[1240px]">
          <CTABanner title={E.cta.title} desc={E.cta.desc} primary={E.cta.primary} secondary={E.cta.secondary} />
        </div>
      </section>

      {heroOpen && (
        <VideoLightbox videoId={FEATURED_VIDEO_ID} title="Gyan Dhara in 75 seconds" onClose={() => setHeroOpen(false)} />
      )}
    </PageShell>
  );
}
